import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { EmailCampaignService } from '../../../shared/services/email-campaign.service';
import { CampaignEmailPreviewDialogComponent } from './campaign-email-preview-dialog/campaign-email-preview-dialog.component';
import {
  CampaignReconciliation,
  CampaignRecipientRow,
  CampaignStatus,
  CampaignSummary,
  getCampaignCreatorName,
} from '../../../shared/models/email-campaign.model';

@Component({
  selector: 'app-campaign-detail',
  templateUrl: './campaign-detail.component.html',
  styleUrls: ['./campaign-detail.component.scss']
})
export class CampaignDetailComponent implements OnChanges, OnDestroy {

  @Input() campaignId: string;
  @Output() closed = new EventEmitter<void>();

  loading = false;
  campaign: CampaignSummary;
  campaignStatus: CampaignStatus;
  pollError: string;
  pollFailed = false;
  recipients: CampaignRecipientRow[] = [];
  recipientsLoading = false;
  recipientsError: string;
  reconciliation: CampaignReconciliation;
  statusFilter = '';

  private _subscriptions: Subscription[] = [];
  private _statusSubscription: Subscription;
  private _recipientsSubscription: Subscription;
  private _allRecipients: CampaignRecipientRow[] = [];

  constructor(
    private _campaignService: EmailCampaignService,
    private _dialog: MatDialog,
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.campaignId && this.campaignId) {
      this.loadDetail();
    }
  }

  ngOnDestroy(): void {
    this._subscriptions.forEach((s) => s.unsubscribe());
    this._subscriptions = [];
    this._statusSubscription?.unsubscribe();
    this._recipientsSubscription?.unsubscribe();
  }

  get creatorName(): string {
    return getCampaignCreatorName(this.campaign?.created_by);
  }

  get sourceGroupNames(): string {
    let groups = this.campaign?.metadata?.source_groups || [];
    return groups.length > 0 ? groups.map((g) => g.name).join(', ') : 'None (individual recipients only)';
  }

  loadDetail() {
    this.loading = true;
    this._subscriptions.push(
      this._campaignService.getCampaign(this.campaignId).subscribe((campaign) => {
        this.campaign = campaign;
        this.loading = false;
      }, (error) => {
        this.loading = false;
        console.log(error);
      })
    );
    this.loadRecipients();
    this._subscriptions.push(
      this._campaignService.getReconciliation(this.campaignId).subscribe((r) => {
        this.reconciliation = r;
      }, (error) => {
        console.log(error);
      })
    );
    // The status bar only ever reads from /status/, never derived from the recipients list.
    // Polls (instead of a single fetch) so an in-progress campaign keeps updating while this
    // page is open, with the same stall/connectivity indication as the send wizard.
    this._statusSubscription?.unsubscribe();
    this._statusSubscription = this._campaignService.pollStatus(this.campaignId).subscribe((res) => {
      this.campaignStatus = res.status;
      this.pollError = res.pollError;
      this.pollFailed = res.pollFailed;
    }, (error) => {
      console.log(error);
    });
  }

  loadRecipients() {
    this._recipientsSubscription?.unsubscribe();
    this._allRecipients = [];
    this.recipients = [];
    this.recipientsLoading = true;
    this.recipientsError = null;
    // Recipient status query parameters are incorrectly validated against campaign
    // statuses by the API, so filter only after all unfiltered pages have loaded.
    this._recipientsSubscription = this._campaignService.getAllRecipients(this.campaignId).subscribe((recipients) => {
      this._allRecipients = recipients;
      this.filterRecipients();
      this.recipientsLoading = false;
    }, (error) => {
      this.recipientsLoading = false;
      this.recipientsError = 'Unable to load the complete recipient list. Please try again.';
      console.log(error);
    });
  }

  filterRecipients() {
    this.recipients = this.statusFilter
      ? this._allRecipients.filter((recipient) => recipient.status === this.statusFilter)
      : this._allRecipients;
  }

  cancelCampaign() {
    this._campaignService.cancelCampaign(this.campaignId).subscribe(() => {
      this.loadDetail();
    }, (error) => {
      console.log(error);
    });
  }

  close() {
    this.closed.emit();
  }

  previewEmail() {
    this._dialog.open(CampaignEmailPreviewDialogComponent, {
      width: '900px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      data: {
        subject: this.campaign?.subject,
        fromEmail: this.campaign?.from_email,
        body: this.campaign?.body_template || '',
      },
    });
  }

  recipientTrackBy(i: number, recipient: CampaignRecipientRow) {
    return recipient.id;
  }
}
